import NiceModal from '@ebay/nice-modal-react';
import { Box, ThemeIcon, Tooltip, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconFileDescription,
  IconHeadphones,
  IconPhoto,
  IconPlayerPlay,
  IconVideo,
} from '@tabler/icons-react';

import { BulkUploadType } from '../constants/mediaConfig';
import { isPersistedPreviewUrl } from '../utils/mediaFile';

import { isPreviewableMedia, MediaPreviewModal } from './MediaPreviewModal';

import { COLORS } from '@/utils/colors';

const TYPE_ICONS: Record<BulkUploadType, typeof IconPhoto> = {
  photo: IconPhoto,
  video: IconVideo,
  audio: IconHeadphones,
  document: IconFileDescription,
};

type Props = {
  mediaType: BulkUploadType;
  previewUrl: string | null;
  title?: string;
  size?: number;
};

export const MediaThumbnail = ({ mediaType, previewUrl, title, size = 48 }: Props) => {
  const TypeIcon = TYPE_ICONS[mediaType];
  const isPreviewable = isPreviewableMedia(mediaType);
  const resolvedUrl =
    previewUrl && (previewUrl.startsWith('blob:') || isPersistedPreviewUrl(previewUrl))
      ? previewUrl
      : null;

  const openPreview = () => {
    if (!isPreviewable || !resolvedUrl) {
      notifications.show({
        color: 'orange',
        title: 'Превью недоступно',
        message: 'Файл ещё не загружен или превью недоступно.',
      });
      return;
    }

    NiceModal.show(MediaPreviewModal, {
      mediaType,
      src: resolvedUrl,
      title: title ?? 'Просмотр',
    });
  };

  const content =
    mediaType === 'photo' && resolvedUrl ? (
      <img
        src={resolvedUrl}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    ) : (
      <ThemeIcon
        variant="transparent"
        size={size}
        color="gray"
        style={{ width: '100%', height: '100%' }}
      >
        {mediaType === 'video' || mediaType === 'audio' ? (
          <IconPlayerPlay size={22} />
        ) : (
          <TypeIcon size={24} />
        )}
      </ThemeIcon>
    );

  const box = (
    <Box
      w={size}
      h={size}
      style={{
        flexShrink: 0,
        borderRadius: 8,
        overflow: 'hidden',
        border: `1px solid ${COLORS.primary}`,
        backgroundColor: 'var(--mantine-color-dark-6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {content}
    </Box>
  );

  if (!isPreviewable) {
    return box;
  }

  return (
    <Tooltip label="Просмотр" withArrow>
      <UnstyledButton
        onClick={openPreview}
        style={{ cursor: 'pointer', lineHeight: 0 }}
        aria-label="Открыть предпросмотр"
      >
        {box}
      </UnstyledButton>
    </Tooltip>
  );
};
